import { filter } from 'rxjs/operators';
import { Component, OnInit, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';

import { CookieService } from 'ngx-cookie-service';

import { AlertService } from '../../../services/common/alert.service';
import { ActionCableService } from '../../../services/common/action-cable.service';
import { AppointmentRequestService } from '../../../services/appointment_requests/appointment-request.service';
import { AuthService } from '../../../services/common/auth.service';
import { DataStoreService } from '../../../services/common/data-store.service';
import { LinkedAccountService } from '../../../services/common/linked-account.service';
import { MatterSearchService } from '../../../services/matters/matter-search.service';
import { NavbarService, MenuItem, NavbarResponse } from '../../../services/navbar/navbar.service';
import { NotyService } from '../../../services/common/noty.service';
import { ScansService } from '../../../services/scans/scans.service';
import { SidekiqService } from '../../../services/common/sidekiq.service';
import { UserService } from '../../../services/users/user.service';
import { AutocompleteResult } from '../../../models/autocomplete-result';

declare var $: any;

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.css'],
})
export class NavbarComponent implements OnInit, OnDestroy {
  /**
   * API Key - used to build iCal URL
   * @type {string}
   */
  api_key: string;

  appointmentRequestsData: any;

  appointmentRequestsIntervalId: number;

  appointmentRequestsLoading: boolean = false;

  appointmentRequestsToView: Array<any>;

  scanQueueChannelSubscription: any;

  scanQueueOpenCount: number = 0;

  scanQueuePendingCount: number = 0;

  /**
   * Attorney Graphs - list of attorneys with names and URLs as JS object
   * @type {any}
   */
  attorneyGraphs: any = [];

  availableIntranets: Array<AutocompleteResult> = []

  canSwitchIntranets: boolean = false;

  switchableUsers: Array<{ id: number; name: string; login: string }> = [];

  userSwitchActive: boolean = false;

  originalUserName: string | null = null;

  /** Development-only clock controls used to exercise attendance rules locally. */
  developmentClockPresets: string[] = ['08:45', '08:52', '09:00', '09:03', '09:15', '10:40'];

  developmentClockBusy: boolean = false;

  developmentClockCurrent: string;

  showDevelopmentClockControls: boolean = false;

  /**
   * Indicates if component has been destroyed to prevent backend request response to attempt to alter the component after being destroyed
   * @type {boolean}
   */
  componentDestroyed: boolean = false;

  /**
   * Current user's contact id
   * @type {number}
   */
  contactId: number;

  /**
   * iCal URL built using API key for current user's events
   * @type {string}
   */
  icalUrl: string;

  icalCopying: boolean = false;

  icalCopied: boolean = false;

  private icalCopyFeedbackTimer: number;

  private icalCopyResetTimer: number;

  /**
   * Current Intranet
   * @type {any}
   */
  intranet: any = {};

  /**
   * Indicates if sidekiq info is being loaded
   * @type {boolean}
   */
  loadingSidekiq: boolean = false;

  /**
   * Matter number field value in the navbar
   * @type {string}
   */
  matter_number: string;

  /**
   * Navbar structure for the first half (upper half) of the navbar
   * @type {any}
   */
  navBarStructure: any = [];

  /**
   * User id and intranet id
   * @type {any}
   */
  navbarData: any = {};

  /**
   * Indicates if new notification is present
   * @type {boolean}
   */
  newNotificationsPresent: boolean = false;

  /**
   * Indicates if a matter is being opened using the matter field in the navbar
   * @type {boolean}
   */
  openingMatter: boolean = false;

  /**
   * Indicates if any pending notifications are present
   * @type {boolean}
   */
  pendingNotificationsPresent: boolean = false;

  /**
   * Navbar structure for the sceond half (lower half) of the navbar
   * @type {any}
   */
  secondMenu: any = [];

  selectedIntranet: AutocompleteResult;

  showAptReqMobile: boolean = false;

  showAptReq: boolean = false;

  /**
   * Show notifications sidebar
   * @type {boolean}
   */
  showNotifications: boolean = false;

  /**
   * Sidekiq info in the navbar
   * @type {any}
   */
  sidekiqInfo: any;

  /**
   * Indicates if current user is to be notified if sidekiq is not running
   * @type {boolean}
   */
  showSidekiqNotRunning: boolean = false;

  /**
   * Current user
   * @type {any}
   */
  user: any = {};

  /**
   * Current user groups
   * @type {Array<any>}
   */
  userGroups: Array<any> = [];

  /**
   * Current user login
   * @type {string}
   */
  userLogin: string;

  /**
   * Current user id
   * @type {number}
   */
  userId: number;

  /**
   * Constructor
   * @param {AlertService}        private alertService        Bootstrap alert service
   * @param {AuthService}         private authService         Auth service for fetching user details and groups
   * @param {ChangeDetectorRef}   private changeDetectorRef   Detects changes for UI
   * @param {CookieService}       private cookieService       Store/Retrieve cookies
   * @param {MatterSearchService} private matterSearchService Matter search for opening a new matter
   * @param {NotyService}         private notyService         Notification service
   * @param {Router}              private router              Angular router
   * @param {SidekiqService}      private sidekiqService      Sidekiq info and controls to the backend
   * @param {UserService}         private userService         User resource service
   */
  constructor(
    private actionCableService: ActionCableService,
    private alertService: AlertService,
    private appointmentRequestService: AppointmentRequestService,
    private authService: AuthService,
    private changeDetectorRef: ChangeDetectorRef,
    private dataStoreService: DataStoreService,
    private cookieService: CookieService,
    private linkedAccountService: LinkedAccountService,
    private matterSearchService: MatterSearchService,
    private navbarService: NavbarService,
    private notyService: NotyService,
    private router: Router,
    private scansService: ScansService,
    private sidekiqService: SidekiqService,
    private userService: UserService
  ) {
    this.userService.navbarComponent = this;
    this.dataStoreService.save('navbarComponent', this);
    this.authService.fetchUserGroups().then((response_json) => {
      this.userGroups = response_json;
      this.showDevelopmentClockControls = this.authService.developmentEnv() && this.authService.getUser().is('manager');
      // Fetch api_key for iCal URL
      this.userService.getNavbarData().then((response_json) => {
        this.api_key = response_json.key;
        this.icalUrl = this.buildIcalUrl(this.api_key);
      });

      // Fetch navbar menu from Rails API (single source of truth)
      this.navbarService.getMenu().then((response: NavbarResponse) => {
        this.intranet = response.intranet;
        this.navbarData = response.navbar_data;
        this.user = response.user;
        this.switchableUsers = response.switchable_users || [];
        this.userSwitchActive = !!(response.user_switch && response.user_switch.active);
        this.originalUserName = response.user_switch && response.user_switch.original_user;
        this.attorneyGraphs = response.attorneys.map(a => ({ name: a.name, url: `/graphs/attorneys?id=${a.id}`, show: true }));

        if (this.authService.getUser().is('priviledged')) {
          this.linkedAccountService.intranets()
            .then(intranets_response => {
              this.availableIntranets = intranets_response.intranets;
              this.setCurrentIntranet();
              this.canSwitchIntranets = (this.availableIntranets.length > 0 && !!this.selectedIntranet);
            });
        }

        this.navBarStructure = response.menu_items;
        this.loadScanQueueStatus();
        this.subscribeScanQueueChannel();
        if (this.authService.getUser().is('tech owner')) this.loadSidekiqInfo();
        this.showSidekiqNotRunning = this.authService.getUser().is('tech owner');
        if (!this.componentDestroyed) this.changeDetectorRef.detectChanges();
      });
    });
  }

  buildIcalUrl(apiKey: string): string {
    return `${window.location.origin}/events.ical?salt=${encodeURIComponent(apiKey || '')}`;
  }

  // Component lifecycle events
  /**
   * Called when the comoponent is initialized
   */
  ngOnInit() {
    this.router.events.pipe(filter((event) => event instanceof NavigationStart)).subscribe((event: any) => {
      this.clearSecondMenu();
    });
    this.loadAppointmentRequests();
    this.appointmentRequestsIntervalId = window.setInterval(() => this.loadAppointmentRequests(), 60000);

    // Initialize dropdown submenu behavior after a short delay to ensure DOM is ready
    setTimeout(() => {
      this.initDropdownSubmenus();
    }, 500);
  }

  loadAppointmentRequests(): void {
    if (this.componentDestroyed || this.appointmentRequestsLoading) return;
    this.appointmentRequestsLoading = true;
    this.appointmentRequestService
      .getAnnouncements()
      .then((response_json) => {
        if (!this.componentDestroyed && response_json) this.appointmentRequestsData = response_json;
      })
      .finally(() => {
        this.appointmentRequestsLoading = false;
      });
  }

  developmentClockLabel(preset: string): string {
    const parts = preset.split(':').map((part) => Number(part));
    const hour = parts[0] % 12 || 12;
    return `${hour}:${String(parts[1]).padStart(2, '0')}`;
  }

  setDevelopmentClock(preset: string) {
    if (this.developmentClockBusy) return;

    this.developmentClockBusy = true;
    this.userService.setDevelopmentClock(preset).then((response) => {
      if (response && response.success !== false) {
        this.developmentClockCurrent = response.label || this.developmentClockLabel(preset);
        this.notyService.notify(`Development clock set to ${this.developmentClockCurrent}.`, 'success');
      }
    }).catch(() => {
      this.notyService.notify('Unable to set the development clock.', 'error');
    }).finally(() => {
      this.developmentClockBusy = false;
    });
  }

  resetDevelopmentClock() {
    if (this.developmentClockBusy) return;

    this.developmentClockBusy = true;
    this.userService.resetDevelopmentClock().then(() => {
      this.developmentClockCurrent = 'Real time';
      this.notyService.notify('Development clock reset to real time.', 'success');
    }).catch(() => {
      this.notyService.notify('Unable to reset the development clock.', 'error');
    }).finally(() => {
      this.developmentClockBusy = false;
    });
  }

  openAppointmentRequests(event: Event, appointmentRequests: Array<any>): boolean {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    this.appointmentRequestsToView = appointmentRequests || [];
    this.showAptReq = false;
    setTimeout(() => {
      this.showAptReq = true;
      if (!this.componentDestroyed) this.changeDetectorRef.detectChanges();
    });
    return false;
  }

  /**
   * Initialize dropdown submenu hover behavior
   * This prevents submenus from closing when navigating to nested items
   */
  initDropdownSubmenus() {
    // Handle submenu hover - show submenu and keep parent open
    $(document).off('mouseenter.submenu').on('mouseenter.submenu', '.dropdown-submenu', function(this: HTMLElement, e: any) {
      const $submenu = $(this).find('> .dropdown-menu').first();
      $submenu.addClass('show');
      e.stopPropagation();
    });

    $(document).off('mouseleave.submenu').on('mouseleave.submenu', '.dropdown-submenu', function(this: HTMLElement, e: any) {
      const $submenu = $(this).find('> .dropdown-menu').first();
      $submenu.removeClass('show');
    });

    // Prevent dropdown from closing when clicking inside submenu
    $(document).off('click.submenu').on('click.submenu', '.dropdown-submenu > a', function(this: HTMLElement, e: any) {
      const $next = $(this).next('.dropdown-menu');
      if ($next.length > 0) {
        e.preventDefault();
        e.stopPropagation();
        $next.toggleClass('show');
      }
    });
  }

  /**
   * Called when the component is destroyed
   */
  ngOnDestroy() {
    if (this.scanQueueChannelSubscription) this.scanQueueChannelSubscription.unsubscribe();
    if (this.appointmentRequestsIntervalId) window.clearInterval(this.appointmentRequestsIntervalId);
    if (this.icalCopyFeedbackTimer) window.clearTimeout(this.icalCopyFeedbackTimer);
    if (this.icalCopyResetTimer) window.clearTimeout(this.icalCopyResetTimer);
    this.dataStoreService.remove('navbarComponent');
    this.componentDestroyed = true;
  }

  // Component methods
  /**
   * Clear sidekiq counters in redis
   */
  clearCountersSidekiq() {
    if (this.loadingSidekiq) return;
    this.loadingSidekiq = true;
    this.sidekiqService
      .clearCounters()
      .then((response_json) => {
        this.loadingSidekiq = false;
        if (response_json.success) {
          this.notyService.notify(response_json.text || 'Counters reset', 'success');
          this.loadSidekiqInfo();
        } else this.notyService.notify(response_json.text || 'Error resetting counters', 'error');
      })
      .catch(() => {
        this.loadingSidekiq = false;
        this.notyService.notify('Error resetting counters', 'error');
      });
  }

  /**
   * Clear reset queue in sidekiq
   */
  clearResetQueueSidekiq() {
    if (this.loadingSidekiq) return;
    this.loadingSidekiq = true;
    this.sidekiqService
      .clearResetQueue()
      .then((response_json) => {
        this.loadingSidekiq = false;
        if (response_json.success) {
          this.notyService.notify(response_json.text || 'Retries cleared', 'success');
          this.loadSidekiqInfo();
        } else this.notyService.notify(response_json.text || 'Error clearing sidekiq retries', 'error');
      })
      .catch(() => {
        this.loadingSidekiq = false;
        this.notyService.notify('Error clearing sidekiq retries', 'error');
      });
  }

  /**
   * Clear second half of menu
   */
  clearSecondMenu() {
    this.secondMenu = [];
  }

  /**
   * Indicates if iCal URL has been copied to the clipboard
   */
  copiedIcal() {
    this.notyService.notify('iCal copied', 'success');
    if (this.icalCopyFeedbackTimer) window.clearTimeout(this.icalCopyFeedbackTimer);
    this.icalCopyFeedbackTimer = window.setTimeout(() => {
      this.icalCopying = false;
      this.icalCopied = true;
      this.icalCopyResetTimer = window.setTimeout(() => (this.icalCopied = false), 1400);
    }, 350);
  }

  copyIcal() {
    this.startIcalCopy();
    this.writeTextToClipboard(this.icalUrl)
      .then(() => this.copiedIcal())
      .catch(() => this.copyIcalFailed());
  }

  copyIcalFailed() {
    this.icalCopying = false;
    this.icalCopied = false;
    this.notyService.notify('Unable to copy the iCal URL', 'error');
  }

  startIcalCopy() {
    if (this.icalCopyFeedbackTimer) window.clearTimeout(this.icalCopyFeedbackTimer);
    if (this.icalCopyResetTimer) window.clearTimeout(this.icalCopyResetTimer);
    this.icalCopied = false;
    this.icalCopying = true;
  }

  private writeTextToClipboard(text: string): Promise<void> {
    if (navigator.clipboard && navigator.clipboard.writeText) return navigator.clipboard.writeText(text);

    return new Promise<void>((resolve, reject) => {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();

      try {
        document.execCommand('copy') ? resolve() : reject(new Error('Clipboard copy failed'));
      } catch (error) {
        reject(error);
      } finally {
        document.body.removeChild(textarea);
      }
    });
  }

  /**
   * Check if nav item is to be displayed for current user
   * @param  {any}     navItem nav item to display
   * @return {boolean}         indicates if nav item is to be displayed
   */
  displayNavItem(navItem: any): boolean {
    try {
      return (navItem.grouped && this.authService.getUser().is(navItem.grouped)) || (navItem.groupedNot && !this.authService.getUser().is(navItem.groupedNot)) || (!navItem.grouped && !navItem.groupedNot);
    } catch (err) {
      console.error(err);
      return true;
    }
  }

  /**
   * Kill Sidekiq processes
   */
  killSidekiq() {
    if (this.loadingSidekiq) return;
    this.loadingSidekiq = true;
    this.sidekiqService
      .killSidekiq()
      .then((response_json) => {
        this.loadingSidekiq = false;
        if (response_json.success) {
          this.notyService.notify(response_json.text || 'Sidekiq shut down', 'success');
          this.loadSidekiqInfo();
        } else this.notyService.notify(response_json.text || 'Error shutting down sidekiq', 'error');
      })
      .catch(() => {
        this.loadingSidekiq = false;
        this.notyService.notify('Error shutting down sidekiq', 'error');
      });
  }

  /**
   * Load Sidekiq info
   */
  loadSidekiqInfo() {
    if (this.loadingSidekiq) return;
    this.loadingSidekiq = true;
    this.sidekiqService
      .sidekiqInfo()
      .then((response_json) => {
        this.loadingSidekiq = false;
        this.sidekiqInfo = response_json;
      })
      .catch(() => {
        this.loadingSidekiq = false;
      });
  }

  loadScanQueueStatus() {
    this.scansService.queueStatus().then((response_json) => {
      this.updateScanQueueStatus(response_json);
    });
  }

  subscribeScanQueueChannel() {
    if (!this.navbarData || !this.navbarData.intranet_id || this.scanQueueChannelSubscription) return;

    this.actionCableService.cable().then((cable: any) => {
      this.scanQueueChannelSubscription = cable.subscriptions.create(
        { channel: 'ScanQueueChannel' },
        {
          connected: () => {
            console.log('Subscribed to ScanQueueChannel');
          },
          received: (data: any) => {
            if (data.action === 'scan_queue_status' && data.intranets) this.updateScanQueueStatus(data);
            else this.loadScanQueueStatus();
          },
          rejected: () => {
            console.log('ScanQueueChannel subscription rejected');
          },
        }
      );
    });
  }

  updateScanQueueStatus(data: any) {
    this.scanQueueOpenCount = Number(data && data.open_count) || 0;
    this.scanQueuePendingCount = Number(data && data.pending_count) || 0;
    if (!this.componentDestroyed) this.changeDetectorRef.detectChanges();
  }

  /**
   * Logout
   */
  logout(final: boolean = true) {
    this.alertService.alert_status_info_overlay('Logging out');
    this.authService
      .logout()
      .then((response_json) => {
        if (response_json && response_json.reason_required) {
          this.alertService.alert_status_clear_overlay();
          const notice = response_json.attendance_notice || {};
          if (window.confirm('Are you logging out for a lunch break? Click OK to record a lunch logout, or Cancel to continue with the early-departure reason.')) {
            this.logoutForLunch();
            return;
          }
          const reason = window.prompt(
            `You are ending your workday at ${notice.signed_out_at || 'this time'}, before your scheduled end of ${notice.scheduled_end || 'the scheduled end'}. This creates ${notice.early_minutes || 0} unpaid minutes. Please explain why you are leaving early:`,
            ''
          );
          if (!reason || !reason.trim()) {
            this.notyService.notify('Logout cancelled. A reason is required when ending your workday early.', 'warning');
            return;
          }
          this.logoutWithEarlyReason(reason.trim());
        } else if (response_json.success) {
          this.alertService.alert_status_clear_overlay();
        }
      })
      .catch(() => {
        this.alertService.alert_status_clear_overlay();
      });
  }

  private logoutWithEarlyReason(reason: string) {
    this.alertService.alert_status_info_overlay('Logging out');
    this.authService.logout(reason).then((response_json) => {
      if (response_json && response_json.success) this.alertService.alert_status_clear_overlay();
    }).catch(() => {
      this.alertService.alert_status_clear_overlay();
    });
  }

  private logoutForLunch() {
    this.alertService.alert_status_info_overlay('Logging out for lunch');
    this.authService.logout('', true).then((response_json) => {
      if (response_json && response_json.success) this.alertService.alert_status_clear_overlay();
    }).catch(() => this.alertService.alert_status_clear_overlay());
  }

  /**
   * Force a full page reload to bypass Angular routing
   * Used for Rails views that should not be handled by Angular
   */
  forceReload(event: Event, url: string): boolean {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    // Append JWT token from localStorage for Rails authentication
    const token = window.localStorage.getItem('token');
    const separator = url.includes('?') ? '&' : '?';
    window.location.href = `${url}${separator}token=${encodeURIComponent(token || '')}`;
    return false;
  }

  /**
   * Open matter using the navbar matter number field
   */
  openMatter(newTab: boolean = false) {
    if (this.matter_number) {
      this.alertService.alert_status_info_overlay('Finding matter');
      this.matterSearchService
        .findByNumber(this.matter_number)
        .then((response_json) => {
          this.alertService.alert_status_clear_overlay();
          if (response_json.success) {
            if (newTab) window.open(`/matters/${response_json.id}`, '_blank');
            else this.router.navigateByUrl(`/matters/${response_json.id}`);
            this.matter_number = '';
          } else this.alertService.alert_status_warning('Matter not found');
        })
        .catch(() => {
          this.alertService.alert_status_clear_overlay();
          this.alertService.alert_status_warning('Error finding matter');
        });
    }
  }

  /**
   * Parse URL
   * @param  {any}    arr params
   * @return {string}     URL
   */
  parseURL(arr: any): string {
    return `${arr[0]}?${arr[1]}=${this.navbarData[arr[1]]}`;
  }

  /**
   * Set menu for accounting and related pages
   */
  setAccountingMenu() {
    this.authService.fetchUserGroups().then((response_json) => {
      this.userGroups = response_json;
      this.secondMenu = [
        { name: 'Billings', url: '/billings', show: true },
        { name: 'Bills', url: '/bills', show: true },
        { name: 'Checks', url: '/checks', show: true },
        { name: 'Deposits', url: '/deposits', show: true },
        { name: 'Entry Groups', url: '/entry_groups', show: true },
        { name: 'Factors', url: '/factors', show: true },
        { name: 'Invoices', url: '/invoices', show: true },
        { name: 'Ledgers', url: '/ledgers', show: true },
        { name: 'Online Payments', routerURL: '/online_payments', show: true },
        { name: 'Receipts', url: '/receipts', show: true },
        { name: 'Receivables', url: '/receivables', show: true },
        { name: 'Referrals', url: '/referrals', show: true },
        { name: 'Wages', url: '/wages', show: true },
      ];
    });
  }

  /**
   * Set menu for document templates and related pages
   */
  setDocumentTemplatesMenu() {
    this.authService.fetchUserGroups().then((response_json) => {
      this.userGroups = response_json;
      this.secondMenu = [
        {
          name: 'Document Templates',
          url: '#',
          show: true,
          children: [
            { name: 'Index', routerURL: '/doc_templates', show: true },
            { name: 'New', routerURL: '/doc_templates/new', show: true },
          ],
        },
        {
          name: 'Stylesheets',
          url: '#',
          show: true,
          children: [
            { name: 'Index', url: '/stylesheets', show: true },
            { name: 'New', url: '/stylesheets/new', show: true },
          ],
        },
        { name: 'Stylesheet Groups', url: '/stylesheet_groups', show: true },
        { name: 'Fonts', url: '/fonts', show: true },
      ];
    });
  }

  /**
   * Set Second Menu as home page menu
   * @param {string} user_login current user login
   * @param {number} user_id    current user id
   * @param {number} contact_id current user contact id
   */
  setHomeMenu(user_login: string | null = null, user_id: number | null = null, contact_id: number | null = null) {
    if (user_login) this.userLogin = user_login;
    if (user_id) this.userId = user_id;
    if (contact_id) this.contactId = contact_id;
    this.authService.fetchUserGroups().then((response_json) => {
      this.userGroups = response_json;
      this.secondMenu = [
        {
          name: 'Accounting',
          url: '#',
          show: true,
          children: [
            { name: 'Activity Rates', url: `/activity_rates?user_id=${this.userId}`, show: true },
            { name: 'Escrow Balances', url: `/escrow_balances?user_id=${this.userId}`, show: this.authService.getUser().is('attorney tech') },
            { name: 'Billings', url: `/billings?user_id=${this.userId}&attorney_id=${this.contactId}`, show: this.authService.getUser().is('attorney tech') },
            { name: 'Factors', url: `/factors?user_id=${this.userId}&attorney_id=${this.contactId}`, show: this.authService.getUser().is('attorney tech') },
            { name: 'Graphs', url: `/graphs/attorneys?id=${this.userId}`, show: this.authService.getUser().is('attorney tech') },
            { name: 'Leave', url: `/staff_leave_requests?user_id=${this.userId}`, show: this.authService.getUser().is('staff manager') },
            { name: 'Receivables', url: `/receivables`, show: this.authService.getUser().is('attorney tech') },
            { name: 'Upcoming Events', parseURL: `/users/upcoming_events?user_id=${user_id}`, show: this.authService.getUser().is('attorney tech') },
            { name: 'Upcoming Hearings', parseURL: `/users/upcoming_hearings?user_id=${user_id}`, show: this.authService.getUser().is('attorney tech') },
          ],
        },
        { name: 'Contact', routerURL: `/contacts/${this.contactId}`, show: true },
        { name: 'Email', url: `/email_archives?to=${this.userLogin}`, show: true },
        { name: 'iCal', clipboard: true, show: true },
        { name: 'User', routerURL: `/users/${this.userId}/edit`, show: true },
      ];
    });
  }

  /**
   * Set Second Menu as Matter Show page
   * @param {number} matter_id     Matter id of the matter show
   * @param {number} matter_number Matter number of the matter page
   */
  setMatterShowMenu(matter_id: number, matter_number: number) {
    this.authService.fetchUserGroups().then((response_json) => {
      this.userGroups = response_json;
      this.secondMenu = [
        { name: `Matter: ${matter_number}`, routerURL: `/matters/${matter_id}`, show: true },
        {
          name: 'Accounting',
          url: '#',
          show: true,
          children: [
            { name: 'Activity Rates', url: `/activity_rates?matter_id=${matter_id}`, show: true },
            { name: 'AI Usage', url: `/ai_usage_logs?matter_id=${matter_id}`, show: true },
            { name: 'Analyze Bills', url: `/bills/analyze?matter_id=${matter_id}`, show: true },
            { name: 'Applicable Rates', url: `/matters/${matter_id}/applicable_rates`, show: true },
            { name: 'Credit Account', url: `/bills/${matter_id}/credit_account?matter_id=${matter_id}`, show: true },
            { name: 'Invoices', url: `/invoices.${matter_id}`, show: true },
            { name: 'Receipts', url: `/receipts?matter_id=${matter_id}`, show: true },
            { name: 'Statement', url: `/outgoing_invoices/${matter_id}`, show: true },
            { name: 'Weekly Rates', url: `/matters/${matter_id}/weekly_rates`, show: true },
          ],
        },
        {
          name: 'Documents',
          url: '#',
          show: true,
          children: [{ name: 'Media', url: `/matters/${matter_id}/media`, show: true }],
        },
        {
          name: 'New',
          url: '#',
          show: true,
          children: [
            { name: 'Appointment Request', routerURL: `matters/${matter_id}/appointment_requests/new`, show: this.authService.getUser().is('attorney admin tech') },
            { name: 'Email', url: `/email/new?matter_id=${matter_id}`, show: true },
            { name: 'Events', url: `/events/new?matter_id=${matter_id}`, show: true },
            { name: 'Fill In Form', url: `https://attymatter.com/forms/fill_in_form?matter_id=${matter_id}`, show: true },
            { name: 'File Share', url: `/matters/${matter_id}/shares/new`, show: true },
            { name: 'Oral Deposition', routerURL: `/matters/${matter_id}/oral_depositions/new`, show: this.authService.getUser().is('attorney admin tech') },
            { name: 'Paystub', url: `https://attymatter.com/matters/${matter_id}/paystubs/new`, show: true },
            { name: 'Upload Media', url: `/matters/${matter_id}/media/new`, show: true },
          ],
        },
        { name: 'Portal', url: `/share_sessions/impersonate?matter_id=${matter_id}`, target: '_blank', show: true },
        {
          name: 'Queues',
          url: '#',
          show: true,
          children: [
            { name: 'Appointment Request', routerURL: `matters/${matter_id}/appointment_requests`, show: true },
            { name: 'Exam Requests', url: `https://attymatter.com/exam_requests?matter_search=${matter_id}`, show: true },
            { name: 'Oral Deposition Queue', routerURL: `matters/${matter_id}/oral_depositions/`, show: true },
            { name: 'Paystub Queue', url: `https://attymatter.com/matters/${matter_id}/paystubs/`, show: true },
          ],
        },
      ];
    });
  }

  /**
   * Set Second Menu as to be paids menu
   */
  setPaymentsMenu() {
    this.authService.fetchUserGroups().then((response_json) => {
      this.userGroups = response_json;
      this.secondMenu = [
        { name: 'Charges', url: '/payroll_charges', show: true },
        { name: 'Payroll Factor Charges', url: '/payroll_factor_charges', show: true },
        { name: 'Vendors', routerURL: '/vendors', show: true },
      ];
    });
  }

  /**
   * Set Second Menu for taxes
   */
  setTaxesMenu() {
    this.authService.fetchUserGroups().then((response_json) => {
      this.userGroups = response_json;
      this.secondMenu = [
        { name: 'Obligations', routerURL: '/tax_obligations', show: true },
        { name: 'Taxes', routerURL: '/taxes', show: true },
        { name: 'Global Taxes', routerURL: '/global_taxes', show: true },
        { name: 'Witheld Income Taxes', routerURL: '/withheld_income_taxes', show: true },
        { name: 'Refund', routerURL: '/tax_refund/new', show: true },
      ];
    });
  }

  /**
   * Start Sidekiq processes
   */
  startSidekiq() {
    if (this.loadingSidekiq) return;
    this.loadingSidekiq = true;
    this.sidekiqService
      .startSidekiq()
      .then((response_json) => {
        this.loadingSidekiq = false;
        if (response_json.success) {
          this.notyService.notify(response_json.text || 'Sidekiq started', 'success');
          this.loadSidekiqInfo();
        } else this.notyService.notify(response_json.text || 'Error starting sidekiq', 'error');
      })
      .catch(() => {
        this.loadingSidekiq = false;
        this.notyService.notify('Error starting sidekiq', 'error');
      });
  }
  
  get intranetId(): string | null | undefined {
    return (
      window.sessionStorage.getItem('intranetId') || 
      window.localStorage.getItem('intranetId') || 
      this.cookieService.get('intranetId')
    );
  }

  get backendIntranet(): AutocompleteResult {
    let selectedIntranet = new AutocompleteResult();
    selectedIntranet.label = this.intranet.navbar_label || this.intranet.short_name;
    selectedIntranet.value = this.intranet.id;
    return selectedIntranet;
  }

  setCurrentIntranet(): void {
    let intranetId: string | null | undefined = this.intranetId;
    console.log("Current intranet", intranetId);
    console.log("Server intranet", this.intranet);
    if(intranetId && intranetId != 'null' && intranetId != 'undefined') {
      console.log('unblock')
      if(this.intranet.id == intranetId){
        this.setIntranet(this.backendIntranet);
        return;
      }
      for(let availableIntranet of this.availableIntranets) {
        if(availableIntranet.value == intranetId){
          this.switchIntranet(availableIntranet);
          break;
        }
      }
    }else{
      console.log('Block executed')
      this.setIntranet(this.backendIntranet);
    }
  }

  setIntranet(intranet: AutocompleteResult): void {
    console.log('setIntranet called with:', JSON.stringify(intranet));
    this.selectedIntranet = new AutocompleteResult();
    this.selectedIntranet.label = intranet.label;
    this.selectedIntranet.value = intranet.value;
    window.sessionStorage.setItem('intranetId', intranet.value);
    window.localStorage.setItem('intranetId', intranet.value);
    this.cookieService.delete('intranetId');
    this.cookieService.delete('intranetId', '/');
    this.cookieService.set('intranetId', intranet.value, undefined, '/');
    document.title = intranet.label;
    console.log('Selected intranet (JSON):', JSON.stringify(this.selectedIntranet));
    console.log('Selected intranet (label):', this.selectedIntranet.label, 'value:', this.selectedIntranet.value);
    console.log('intranets 2', window.sessionStorage.getItem('intranetId'),
    window.localStorage.getItem('intranetId'),
    this.cookieService.get('intranetId'));
  }

  switchIntranet(intranet: AutocompleteResult): void {
    console.log("Switching intranet", intranet);
    this.setIntranet(intranet);
    console.log("Final intranet", this.intranetId);
    window.location.reload();
  }

  switchUser(userId: number): void {
    this.userService.switchUser(userId).then(() => window.location.reload());
  }

  switchUserBack(): void {
    this.userService.switchUserBack().then(() => window.location.reload());
  }

  updateNotifications(): void {
    this.dataStoreService.updateReminderComponents();
  }
}
